// File: src/components/albums/CreateAlbumModal.js

import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useProfile } from '../../context/ProfileContext';
import toast from 'react-hot-toast';

const CreateAlbumModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkPreview, setArtworkPreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { user, activeProfile, isDarkMode } = useProfile(); 

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setArtworkFile(file);
      setArtworkPreview(URL.createObjectURL(file));
    }
  };

  const handleCreate = async () => {
    if (!activeProfile?.id || activeProfile?.type !== 'artist') {
      return toast.error("You must be on your artist profile to create an album.");
    }
    if (!name.trim() || !artworkFile) {
      return toast.error("Album name and artwork are required.");
    }

    setIsLoading(true);
    try {
      const token = await user.getIdToken();

      const generateUrlResponse = await fetch("https://us-central1-atomic-musicapp.cloudfunctions.net/generateAlbumArtworkUploadUrl", {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          artistId: activeProfile.id,
          fileName: artworkFile.name,
          contentType: artworkFile.type,
        }),
      });

      if (!generateUrlResponse.ok) {
        throw new Error('Could not get upload permission.');
      }
      
      const { uploadUrl, finalPath } = await generateUrlResponse.json();

      await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': artworkFile.type },
        body: artworkFile,
      });

      const storage = getStorage();
      const artworkUrl = await getDownloadURL(ref(storage, finalPath));

      const db = getFirestore();
      await addDoc(collection(db, 'albums'), {
        name,
        description,
        artistId: activeProfile.id,
        artworkUrl, 
        songIds: [],
        createdAt: serverTimestamp(),
      });

      toast.success(`Album "${name}" created!`);
      handleClose();
    } catch (err) {
      console.error("Error creating album:", err);
      toast.error('Failed to create album. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setArtworkFile(null);
    setArtworkPreview(null);
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  // 🌟 REACT PORTAL: Isolates overlay DOM tree at document.body level
  return createPortal(
    <AnimatePresence mode="wait">
      {isOpen && (
        <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 ${isDarkMode ? 'dark' : ''}`}>
          
          {/* 🌟 SOLID BACKDROP OVERLAY (GPU-friendly, no blur repaint glitches) */}
          <motion.div
            className="fixed inset-0 bg-black/75 transform-gpu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "linear" }}
            onClick={handleClose}
          />

          {/* 🌟 MODAL CARD: Hardware-accelerated transitions */}
          <motion.div
            className="relative z-10 bg-white dark:bg-brand-black rounded-3xl shadow-2xl p-8 w-full max-w-sm border border-slate-100 dark:border-brand-gray text-slate-900 dark:text-brand-white transform-gpu"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-5">Create New Album</h2>
            
            <div className="space-y-4">
              {/* Album Name Input */}
              <input 
                type="text" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Album Name" 
                className="w-full p-4 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl dark:text-brand-white placeholder-slate-400 dark:placeholder-brand-white/50 focus:ring-2 focus:ring-brand-pink focus:border-transparent outline-none transition-all" 
              />
              
              {/* Description Input */}
              <textarea 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Description (optional)" 
                rows="3" 
                className="w-full p-4 bg-slate-50 dark:bg-brand-gray/40 border border-slate-300 dark:border-brand-gray rounded-2xl dark:text-brand-white placeholder-slate-400 dark:placeholder-brand-white/50 focus:ring-2 focus:ring-brand-pink focus:border-transparent outline-none transition-all resize-none" 
              />
              
              {/* File Input and Artwork Preview Area */}
              <div className="p-4 bg-slate-50 dark:bg-brand-gray/20 border border-slate-200 dark:border-brand-gray/60 rounded-2xl">
                <label className="block text-xs font-black uppercase tracking-wider text-gray-500 dark:text-brand-white/50 mb-2">
                  Album Artwork
                </label>
                <input 
                  type="file" 
                  accept="image/*" 
                  onChange={handleFileChange} 
                  className="block w-full text-xs text-gray-500 dark:text-brand-white/40 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-black file:uppercase file:tracking-wider file:bg-brand-pink/10 file:text-[#ff00cc] hover:file:bg-brand-pink/20 cursor-pointer" 
                />
                {artworkPreview && (
                  <div className="mt-3 flex justify-center">
                    <img src={artworkPreview} alt="Artwork preview" className="w-24 h-24 rounded-2xl object-cover shadow-md border border-slate-200 dark:border-brand-gray" />
                  </div>
                )}
              </div>
            </div>

            {/* Footer Control Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <button 
                onClick={handleClose} 
                className="px-4 py-2 rounded-md text-gray-600 dark:text-brand-white/50 hover:bg-gray-100 dark:hover:bg-brand-gray/20 transition-colors text-sm font-semibold"
              >
                Cancel
              </button>
              <button 
                onClick={handleCreate} 
                disabled={isLoading} 
                className="px-5 py-2 rounded-xl bg-brand-pink text-white font-bold text-sm shadow-md hover:brightness-95 disabled:bg-gray-400 dark:disabled:bg-slate-800 transition-all active:scale-95"
              >
                {isLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default CreateAlbumModal;